export type OpeningStatus = 'draft' | 'prepared' | 'submitted' | 'approved' | 'posted';
export type OpeningAction = 'prepare' | 'submit' | 'approve' | 'post';
export interface OpeningItem {
  itemId: string;
  kind: 'ar' | 'ap' | 'stock';
  sourceRowId: string;
  sourceDocumentReference: string;
  openingDate: string;
  outstandingAmountRial?: string | null;
  totalValueRial?: string | null;
  quantityNum?: string | null;
  quantityDen?: string | null;
  warehouseId?: string | null;
  productId?: string | null;
  controlAccountId?: string | null;
  counterpartAccountId?: string | null;
  controlDimensions?: readonly {
    dimensionId: string;
    dimensionValueId: string;
  }[];
  counterpartDimensions?: readonly {
    dimensionId: string;
    dimensionValueId: string;
  }[];
  warehouseLocationId?: string | null;
  lotId?: string | null;
  serialId?: string | null;
  journalId?: string | null;
  stockMovementId?: string | null;
}
export interface OpeningMapping {
  itemId: string;
  controlAccountId: string;
  counterpartAccountId: string;
  controlDimensions?: Record<string, string>;
  counterpartDimensions?: Record<string, string>;
  warehouseLocationId?: string;
  lotId?: string;
  serialId?: string;
}
export interface OpeningBatch {
  batchId: string;
  status: OpeningStatus;
  reconciliationStatus: string;
  evidence?: string | null;
  fiscalYearId?: string | null;
  fiscalPeriodId?: string | null;
  items: readonly OpeningItem[];
  actions: Readonly<Record<OpeningAction, boolean>>;
  choices: {
    fiscalYears: readonly { id: string; label: string }[];
    fiscalPeriods: readonly {
      id: string;
      fiscalYearId: string;
      label: string;
    }[];
    accounts: readonly { id: string; code: string; name: string }[];
    dimensions: readonly {
      id: string;
      label: string;
      values: readonly { id: string; label: string }[];
    }[];
    accountDimensionRules: readonly {
      accountId: string;
      dimensionId: string;
      required: boolean;
    }[];
    warehouseLocations: readonly {
      id: string;
      warehouseId: string;
      label: string;
    }[];
    lots: readonly { id: string; productId: string; label: string }[];
    serials: readonly { id: string; productId: string; label: string }[];
  };
  sensitiveAction: {
    requiresMfa: boolean;
    available: boolean;
    reason?: string;
  };
}
/** Preserve the server's reviewed mapping when a different actor opens the batch. */
export function openingItemMapping(item: OpeningItem): OpeningMapping {
  return {
    itemId: item.itemId,
    controlAccountId: item.controlAccountId ?? '',
    counterpartAccountId: item.counterpartAccountId ?? '',
    controlDimensions: Object.fromEntries(
      (item.controlDimensions ?? []).map((entry) => [entry.dimensionId, entry.dimensionValueId]),
    ),
    counterpartDimensions: Object.fromEntries(
      (item.counterpartDimensions ?? []).map((entry) => [
        entry.dimensionId,
        entry.dimensionValueId,
      ]),
    ),
    ...(item.warehouseLocationId ? { warehouseLocationId: item.warehouseLocationId } : {}),
    ...(item.lotId ? { lotId: item.lotId } : {}),
    ...(item.serialId ? { serialId: item.serialId } : {}),
  };
}
export interface ImportDestination {
  sourceRowId: string;
  kind: 'party' | 'uom' | 'product' | 'warehouse' | 'sales-order' | 'opening' | 'source-evidence';
  entityId: string;
  code?: string;
  status: 'created' | 'existing' | 'draft' | 'retained';
  module: 'crm' | 'master-data' | 'sales' | 'opening' | 'import';
}
/** Construct only known tenant-local destinations; never trust a response URL. */
export function importDestinationPath(base: string, item: ImportDestination): string | null {
  switch (item.kind) {
    case 'party':
      return item.module === 'crm' && item.code
        ? `${base}/crm/customers/${encodeURIComponent(item.code)}`
        : `${base}/master-data/parties`;
    case 'uom':
      return `${base}/master-data/uoms`;
    case 'product':
      return `${base}/master-data/products`;
    case 'warehouse':
      return `${base}/master-data/warehouses`;
    case 'sales-order':
      return item.code
        ? `${base}/crm/orders/${encodeURIComponent(item.code)}`
        : `${base}/crm/orders`;
    case 'opening':
      return `${base}/data?opening=${encodeURIComponent(item.entityId)}`;
    case 'source-evidence':
      return `${base}/data?workspace=${encodeURIComponent(item.entityId)}`;
    default:
      return null;
  }
}
export function openingPreparationError(
  batch: OpeningBatch,
  fiscalYearId: string,
  fiscalPeriodId: string,
  mappings: readonly OpeningMapping[],
  evidence: string,
): string | null {
  if (
    !batch.choices.fiscalYears.some((year) => year.id === fiscalYearId) ||
    !batch.choices.fiscalPeriods.some(
      (period) => period.id === fiscalPeriodId && period.fiscalYearId === fiscalYearId,
    )
  )
    return 'سال و دورهٔ مالی را انتخاب کنید.';
  if (!evidence.trim()) return 'شرح تطبیق و مستند بررسی را وارد کنید.';
  for (const item of batch.items) {
    const mapping = mappings.find((entry) => entry.itemId === item.itemId);
    if (!mapping) return 'نگاشت حساب‌های تمام ردیف‌ها را تکمیل کنید.';
    for (const side of ['control', 'counterpart'] as const) {
      const accountId = mapping[`${side}AccountId`];
      if (!batch.choices.accounts.some((account) => account.id === accountId))
        return 'حساب‌های هر ردیف را صریح انتخاب کنید.';
      for (const rule of batch.choices.accountDimensionRules.filter(
        (rule) => rule.accountId === accountId && rule.required,
      )) {
        const value = mapping[`${side}Dimensions`]?.[rule.dimensionId];
        if (
          !batch.choices.dimensions
            .find((dimension) => dimension.id === rule.dimensionId)
            ?.values.some((entry) => entry.id === value)
        )
          return 'تفصیلی‌های الزامی حساب‌ها را تکمیل کنید.';
      }
    }
    if (
      item.kind === 'stock' &&
      !batch.choices.warehouseLocations.some(
        (location) =>
          location.id === mapping.warehouseLocationId && location.warehouseId === item.warehouseId,
      )
    )
      return 'محل نگهداری هر ردیف موجودی را انتخاب کنید.';
  }
  return null;
}
